/**
 * Federated (Cognito-brokered SSO) logout wiring.
 *
 * Amplify's `signOut()` clears the local and Cognito sessions but does NOT propagate logout to the
 * upstream OIDC IDP, so a subsequent "Sign in with SSO" would silently re-authenticate against the
 * still-live IDP session. To fully sign out, the federated sign-out redirects through a bounce page
 * ({@link ROUTES.LOGOUT}); once Cognito has cleared its session and returned there, the page
 * forwards the browser to the IDP's end-session endpoint (RP-initiated logout).
 */

import { getConfig } from "../config"
import { ROUTES } from "../utils/constants"

/**
 * Same-origin URL of the logout bounce page. Registered as a Cognito logout URL and used as the
 * sign-out redirect target so Cognito lands the browser here after clearing its session.
 */
export function logoutBounceUrl(): string {
  return `${window.location.origin}${ROUTES.LOGOUT}`
}

/**
 * Build the URL that ends the upstream IDP session (OIDC RP-initiated logout).
 *
 * Discovers the IDP's `end_session_endpoint` at runtime from its OIDC discovery document, then
 * appends `client_id` and `post_logout_redirect_uri` so the IDP returns the browser to the CDR
 * login page after clearing its session.
 *
 * Returns null (caller falls back to a local redirect to /login) when RP-initiated logout does not
 * apply or can't be trusted:
 *
 * - Not federated mode (e.g. the /logout route hit directly in native/direct-oidc),
 * - No OIDC coordinates in config (SAML federation),
 * - The IDP advertises no end-session endpoint, or discovery fails,
 * - The discovered end-session endpoint is not same-origin as the configured issuer (guards against a
 *   compromised/malicious discovery document redirecting the browser off to an attacker origin).
 */
export async function getIdpLogoutUrl(): Promise<string | null> {
  const config = getConfig()
  // Hit outside federated mode (or SAML federation with no OIDC coordinates): nothing to end here.
  if (config.authMode !== "federated") return null
  const { oidcIssuerUrl, oidcClientId } = config
  if (!oidcIssuerUrl || !oidcClientId) return null

  const discoveryUrl = `${oidcIssuerUrl.replace(/\/$/, "")}/.well-known/openid-configuration`

  // These branches are expected graceful-degradation paths (an IDP that advertises no end-session
  // endpoint, or forbids CORS on its discovery document), so they warn rather than error - the
  // caller falls back to /login. The origin-mismatch below is a genuine anomaly and stays at error.
  try {
    const response = await fetch(discoveryUrl, { signal: AbortSignal.timeout(5_000) })
    if (!response.ok) {
      console.warn(`OIDC discovery failed: ${response.status}`)
      return null
    }
    const doc = (await response.json()) as { end_session_endpoint?: string }
    if (!doc.end_session_endpoint) {
      console.warn("IDP discovery document has no end_session_endpoint")
      return null
    }

    const endSession = new URL(doc.end_session_endpoint)
    // The endpoint must live on the same origin as the trusted issuer. Otherwise a compromised
    // discovery document could point the browser at an attacker-controlled origin (CWE-601).
    if (endSession.origin !== new URL(oidcIssuerUrl).origin) {
      console.error(
        `end_session_endpoint origin (${endSession.origin}) does not match issuer origin`,
      )
      return null
    }
    endSession.searchParams.set("client_id", oidcClientId)
    endSession.searchParams.set(
      "post_logout_redirect_uri",
      `${window.location.origin}${ROUTES.LOGIN}`,
    )
    return endSession.toString()
  } catch (error) {
    // Network error / timeout / CORS rejection - an expected fallback, not an app fault.
    console.warn("Could not reach IDP end-session discovery; falling back to /login:", error)
    return null
  }
}
